import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { useToast } from '../../hooks/use-toast';

const InputField = ({ field, value, onChange }) => {
  if (field.type === 'textarea') {
    return (
      <Textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        rows={field.rows || 4}
        className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]"
        data-testid={`field-${field.name}`}
      />
    );
  }
  if (field.type === 'select') {
    return (
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]" data-testid={`field-${field.name}`}>
          <SelectValue placeholder={field.placeholder} />
        </SelectTrigger>
        <SelectContent className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8]">
          {field.options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <div className="flex items-center gap-2">
        <Checkbox
          checked={!!value}
          onCheckedChange={onChange}
          className="border-[#C5A880]/40 data-[state=checked]:bg-[#C5A880] data-[state=checked]:text-[#1F1A17]"
          data-testid={`field-${field.name}`}
        />
        <span className="text-gray-300 text-sm">{field.checkboxLabel || 'Yes'}</span>
      </div>
    );
  }
  if (field.type === 'list') {
    const items = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={`${field.name}-${idx}`} className="flex gap-2">
            <Input
              value={item}
              onChange={(e) => {
                const next = [...items];
                next[idx] = e.target.value;
                onChange(next);
              }}
              className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]"
              data-testid={`field-${field.name}-${idx}`}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              <Trash2 size={16} />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          onClick={() => onChange([...items, ''])}
          className="border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10 w-full"
        >
          <Plus size={16} className="mr-2" />
          Add Item
        </Button>
      </div>
    );
  }
  if (field.type === 'upgrades') {
    const rows = Array.isArray(value) ? value : [];
    const update = (idx, key, v) => onChange(rows.map((r, i) => (i === idx ? { ...r, [key]: v } : r)));
    return (
      <div className="space-y-3">
        {rows.map((row, idx) => (
          <div key={`upg-${idx}`} className="bg-[#1A1612] border border-[#C5A880]/20 rounded-lg p-3 space-y-2">
            <div className="flex gap-2">
              <Input value={row.name || ''} onChange={(e) => update(idx, 'name', e.target.value)} placeholder="Upgrade name" className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8]" data-testid={`upgrade-name-${idx}`} />
              <Button type="button" variant="outline" onClick={() => onChange(rows.filter((_, i) => i !== idx))} className="border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 size={16} /></Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input value={row.price_label || ''} onChange={(e) => update(idx, 'price_label', e.target.value)} placeholder="Display price e.g. $150" className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8]" />
              <Input type="number" value={row.amount ?? 0} onChange={(e) => update(idx, 'amount', Number(e.target.value))} placeholder="Amount (numeric)" className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8]" />
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-gray-300">
              <label className="flex items-center gap-2"><Checkbox checked={!!row.variable} onCheckedChange={(v) => update(idx, 'variable', !!v)} className="border-[#C5A880]/40 data-[state=checked]:bg-[#C5A880] data-[state=checked]:text-[#1F1A17]" /> Variable (quoted separately)</label>
              <label className="flex items-center gap-2"><Checkbox checked={row.discountable !== false} onCheckedChange={(v) => update(idx, 'discountable', !!v)} className="border-[#C5A880]/40 data-[state=checked]:bg-[#C5A880] data-[state=checked]:text-[#1F1A17]" /> Discountable</label>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={() => onChange([...rows, { name: '', price_label: '', amount: 0, variable: false, discountable: true }])} className="border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10 w-full">
          <Plus size={16} className="mr-2" /> Add Upgrade
        </Button>
      </div>
    );
  }
  return (
    <Input
      type={field.type || 'text'}
      value={value || ''}
      onChange={(e) =>
        onChange(field.type === 'number' ? Number(e.target.value) : e.target.value)
      }
      placeholder={field.placeholder}
      className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]"
      data-testid={`field-${field.name}`}
    />
  );
};

const ItemForm = ({ open, onOpenChange, item, fields, onSave, title }) => {
  const [data, setData] = useState({});

  useEffect(() => {
    if (open) {
      const initial = {};
      fields.forEach((f) => {
        initial[f.name] = item?.[f.name] ?? (f.type === 'list' || f.type === 'upgrades' ? [] : f.type === 'checkbox' ? false : f.type === 'number' ? 0 : '');
      });
      setData(initial);
    }
  }, [open, item, fields]);

  const handleSave = async () => {
    // Clean up list items (remove empty strings)
    const cleaned = { ...data };
    fields.forEach((f) => {
      if (f.type === 'list' && Array.isArray(cleaned[f.name])) {
        cleaned[f.name] = cleaned[f.name].filter((x) => x && x.trim());
      }
      if (f.type === 'upgrades' && Array.isArray(cleaned[f.name])) {
        cleaned[f.name] = cleaned[f.name]
          .filter((u) => u && u.name && u.name.trim())
          .map((u) => ({
            name: u.name.trim(),
            price_label: u.price_label || '',
            amount: Number(u.amount) || 0,
            variable: !!u.variable,
            discountable: u.discountable !== false,
          }));
      }
    });
    await onSave(cleaned);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#EADBC8]">{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {fields.map((f) => (
            <div key={f.name}>
              <label className="block text-[#EADBC8] text-sm font-medium mb-2">
                {f.label}
                {f.required && <span className="text-red-400 ml-1">*</span>}
              </label>
              <InputField
                field={f}
                value={data[f.name]}
                onChange={(v) => setData((prev) => ({ ...prev, [f.name]: v }))}
              />
              {f.help && <p className="text-gray-500 text-xs mt-1">{f.help}</p>}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-[#3A332E] border-[#C5A880]/20 text-[#EADBC8]">
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold" data-testid="save-btn">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CrudManager = ({
  title,
  description,
  api,
  fields,
  renderItem,
  itemLabel = 'Item',
  groupBy,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const data = await api.list();
      setItems(data);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [api, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (data) => {
    try {
      if (editing) {
        await api.update(editing.id, data);
        toast({ title: 'Updated successfully' });
      } else {
        await api.create(data);
        toast({ title: 'Created successfully' });
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.remove(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast({ title: 'Deleted' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    setShowForm(true);
  };

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
  };

  const renderList = (list) =>
    list.map((item) => (
      <Card key={item.id} className="bg-[#2A2420] border-[#C5A880]/20" data-testid={`item-${item.id}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">{renderItem(item)}</div>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => openEdit(item)}
                className="border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10"
                data-testid={`edit-${item.id}`}
              >
                <Edit size={14} />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid={`delete-${item.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#2A2420] border-[#C5A880]/20">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-[#EADBC8]">Delete this {itemLabel.toLowerCase()}?</AlertDialogTitle>
                    <AlertDialogDescription className="text-gray-400">
                      This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-[#3A332E] text-[#EADBC8] border-[#C5A880]/20">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDelete(item.id)}
                      className="bg-red-500 hover:bg-red-600 text-[#EADBC8]"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardContent>
      </Card>
    ));

  // Group items if groupBy provided
  let content;
  if (groupBy && items.length > 0) {
    const groups = items.reduce((acc, item) => {
      const key = item[groupBy] || 'Other';
      (acc[key] = acc[key] || []).push(item);
      return acc;
    }, {});
    content = (
      <div className="space-y-8">
        {Object.entries(groups).map(([key, group]) => (
          <div key={key}>
            <h2 className="text-xl font-bold text-[#C5A880] mb-3 capitalize">{key}</h2>
            <div className="space-y-3">{renderList(group)}</div>
          </div>
        ))}
      </div>
    );
  } else {
    content = items.length === 0 ? (
      <Card className="bg-[#2A2420] border-[#C5A880]/20">
        <CardContent className="p-12 text-center text-gray-400">
          No {itemLabel.toLowerCase()}s yet. Click "Add" to create one.
        </CardContent>
      </Card>
    ) : (
      <div className="space-y-3">{renderList(items)}</div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#EADBC8] mb-2">{title}</h1>
          {description && <p className="text-gray-400">{description}</p>}
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold"
          data-testid="add-new-btn"
        >
          <Plus size={18} className="mr-2" />
          Add {itemLabel}
        </Button>
      </div>

      {loading ? <div className="text-gray-400">Loading...</div> : content}

      <ItemForm
        open={showForm}
        onOpenChange={(o) => {
          setShowForm(o);
          if (!o) setEditing(null);
        }}
        item={editing}
        fields={fields}
        onSave={handleSave}
        title={editing ? `Edit ${itemLabel}` : `New ${itemLabel}`}
      />
    </div>
  );
};

export default CrudManager;
